import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthPage from './pages/AuthPage';
import StudentDashboard from './pages/StudentDashboard';
import ChaptersPage from './pages/ChaptersPage';
import VideoExtractor from './pages/VideoExtractor';
import QuizPage from './pages/QuizPage';
import ParentDashboard from './pages/ParentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import ProfilePage from './pages/ProfilePage';
import ChapterChat from './pages/ChapterChat';
import MockExamPage from './pages/MockExamPage';
import VideoLibrary from './pages/VideoLibrary';
import AudioVideoPlayer from './pages/AudioVideoPlayer';
import ExplainerVideoPlayer from './pages/ExplainerVideoPlayer';
import GroupStudyPage from './pages/GroupStudyPage';
import ExamCalendar from './pages/ExamCalendar';
import OCRUploadPage from './pages/OCRUploadPage';
import LandingPage from './pages/LandingPage';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ThankYouPage from './pages/ThankYouPage';
import TrialStartedPage from './pages/TrialStartedPage';
// V5 Features
import DailyLearningLoop from './pages/DailyLearningLoop';
import ExamReadiness from './pages/ExamReadiness';
import ParentTestGenerator from './pages/ParentTestGenerator';
import TestPreviewPage from './pages/TestPreviewPage';
import AnswerSheetEvaluator from './pages/AnswerSheetEvaluator';
import SchoolScoreTracker from './pages/SchoolScoreTracker';
import WeeklyReport from './pages/WeeklyReport';
// Service Dashboard
import ServiceDashboard from './pages/ServiceDashboard';
// Admin Panel
import AdminPanel from './pages/AdminPanel';
// Admin Login Page
import AdminLoginPage from './pages/AdminLoginPage';
import { LayoutDashboard, BookOpen, Zap, User, GraduationCap, Film, Users, Calendar, Target, Clock, X } from 'lucide-react';
import './index.css';

// Valid subscription plans
const VALID_SUBSCRIPTIONS = ['basic', 'pro', 'family'];

function AppContent() {
  const { user, loading, api } = useAuth();
  const reactNavigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState('landing');
  const [pageParams, setPageParams] = useState(null);
  
  // Trial status state
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [showTrialBanner, setShowTrialBanner] = useState(true);

  // Check if user has an active subscription
  const hasActiveSubscription = user && VALID_SUBSCRIPTIONS.includes(user.subscription);
  
  // Check if user has an active trial
  const hasActiveTrial = trialStatus?.active === true;
  
  // Combined check: has subscription OR has active trial
  const hasAccess = hasActiveSubscription || hasActiveTrial;
  
  // Check if we're on special routes that should bypass the subscription gate
  const isThankYouRoute = location.pathname === '/thank-you';
  const isTrialStartedRoute = location.pathname === '/trial-started';
  const isServiceRoute = location.pathname.startsWith('/service');
  const isAdminPanelRoute = location.pathname === '/admin-panel';
  const isAdminLoginRoute = location.pathname === '/admin';
  const isSpecialRoute = isThankYouRoute || isTrialStartedRoute || isServiceRoute || isAdminPanelRoute || isAdminLoginRoute;

  // Fetch trial status for users with 'free' subscription
  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (!user || hasActiveSubscription) {
        setTrialStatus(null);
        return;
      }
      
      // Skip trial check for admin/service users - they don't need subscription gates
      if (['admin', 'service_poc'].includes(user.role)) {
        setTrialStatus(null);
        return;
      }
      
      // Only check trial for users without paid subscription
      if (user.subscription === 'free' || !user.subscription) {
        setTrialLoading(true);
        try {
          const res = await api.get('/user/trial-status');
          setTrialStatus(res.data);
        } catch (err) {
          console.error('Failed to fetch trial status:', err);
          setTrialStatus({ active: false, days_remaining: 0 });
        }
        setTrialLoading(false);
      }
    };
    
    fetchTrialStatus();
  }, [user, hasActiveSubscription, api]);

  // Handle hash-based navigation for public pages
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'about') setCurrentPage('about');
      else if (hash === 'privacy') setCurrentPage('privacy');
      else if (hash === 'refund') setCurrentPage('refund');
    };
    
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Custom navigate function that works with both internal state and React Router
  const navigate = (page, params = null) => {
    // For thank-you page, use React Router navigation
    if (page === 'thank-you') {
      reactNavigate('/thank-you', { state: params });
      return;
    }
    
    // For trial-started page, use React Router navigation
    if (page === 'trial-started') {
      reactNavigate('/trial-started', { state: params });
      return;
    }
    
    // For service dashboard, use React Router navigation
    if (page === 'service') {
      reactNavigate('/service');
      return;
    }
    
    // For admin panel, use React Router navigation
    if (page === 'admin-panel') {
      reactNavigate('/admin-panel');
      return;
    }
    
    // For admin login page, use React Router navigation
    if (page === 'admin') {
      reactNavigate('/admin');
      return;
    }
    
    // For other pages, use internal state
    setCurrentPage(page);
    setPageParams(params);
    window.location.hash = '';
    window.scrollTo(0, 0);
    
    // If we're on special routes, navigate back to root
    if (location.pathname === '/thank-you' || location.pathname === '/trial-started' || location.pathname.startsWith('/service') || location.pathname === '/admin-panel' || location.pathname === '/admin') {
      reactNavigate('/');
    }
  };

  // Auto-navigate to dashboard when user logs in with active subscription or trial
  // But preserve special page navigation (thank-you, trial-started)
  useEffect(() => {
    // Skip auto-navigation if on special routes
    if (isSpecialRoute || currentPage === 'thank-you' || currentPage === 'trial-started') {
      return;
    }
    // Wait for trial status to be checked before auto-navigating
    if (trialLoading) {
      return;
    }
    if (user && hasAccess && (currentPage === 'landing' || currentPage === 'auth')) {
      setCurrentPage('dashboard');
    }
  }, [user, hasAccess, currentPage, trialLoading, isThankYouRoute]);

  if ((loading || trialLoading) && !isSpecialRoute) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center animate-pulse-glow">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white font-['Outfit']">StudyClaw</span>
          </div>
          <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }
  
  // SPECIAL ROUTES: Render special pages BEFORE the subscription gate
  // This allows the pages during payment/signup transitions
  if (isThankYouRoute) {
    return <ThankYouPage onNavigate={navigate} />;
  }
  
  if (isTrialStartedRoute) {
    return <TrialStartedPage onNavigate={navigate} />;
  }
  
  // SERVICE DASHBOARD: Protected route for admin/service_poc
  if (isServiceRoute) {
    // Redirect to login if not authenticated
    if (!user) {
      return <AuthPage onNavigateToTrialStarted={(userData) => {
        reactNavigate('/trial-started', { state: userData });
      }} />;
    }
    // Check if user has admin or service_poc role
    if (!['admin', 'service_poc'].includes(user.role)) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-slate-400 mb-4">You don&apos;t have permission to access the Service Dashboard.</p>
            <button
              onClick={() => navigate('dashboard')}
              className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded-xl"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return <ServiceDashboard onNavigate={navigate} />;
  }
  
  // ADMIN LOGIN PAGE: Public route for admin/service team login
  if (isAdminLoginRoute) {
    return <AdminLoginPage onNavigate={navigate} />;
  }
  
  // ADMIN PANEL: Protected route for admin only
  if (isAdminPanelRoute) {
    // Redirect to login if not authenticated
    if (!user) {
      return <AuthPage onNavigateToTrialStarted={(userData) => {
        reactNavigate('/trial-started', { state: userData });
      }} />;
    }
    // Check if user has admin role (not service_poc)
    if (user.role !== 'admin') {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-slate-400 mb-4">Admin access required.</p>
            <button
              onClick={() => navigate('dashboard')}
              className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded-xl"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return <AdminPanel onNavigate={navigate} />;
  }

  // Public pages (accessible without login)
  if (currentPage === 'landing' && !user) {
    return <LandingPage onGetStarted={() => navigate('auth')} />;
  }
  if (currentPage === 'about') {
    return <AboutUsPage onBack={() => navigate(user ? (hasAccess ? 'dashboard' : 'subscription') : 'landing')} />;
  }
  if (currentPage === 'privacy') {
    return <PrivacyPolicyPage onBack={() => navigate(user ? (hasAccess ? 'dashboard' : 'subscription') : 'landing')} />;
  }
  if (currentPage === 'refund') {
    return <RefundPolicyPage onBack={() => navigate(user ? (hasAccess ? 'dashboard' : 'subscription') : 'landing')} />;
  }
  if (currentPage === 'auth' && !user) {
    return <AuthPage onNavigateToTrialStarted={(userData) => {
      reactNavigate('/trial-started', { state: userData });
    }} />;
  }

  if (!user) return <LandingPage onGetStarted={() => navigate('auth')} />;

  // SUBSCRIPTION GATE: If user is logged in but not subscribed AND not on trial, show subscription page
  // Special routes (/thank-you, /trial-started) are already handled above
  if (!hasAccess && !isSpecialRoute && currentPage !== 'thank-you' && currentPage !== 'trial-started') {
    return <SubscriptionPage onSuccess={(redirectTo, orderDetails) => {
      if (redirectTo === 'thank-you' && orderDetails) {
        reactNavigate('/thank-you', { state: orderDetails });
      } else {
        navigate(redirectTo || 'dashboard');
      }
    }} />;
  }
  
  // Show thank-you page if that's the current page (legacy state-based)
  if (currentPage === 'thank-you') {
    return <ThankYouPage onNavigate={navigate} />;
  }

  const isFullscreen = currentPage === 'chat';

  const renderPage = () => {
    // Handle subscription page navigation from within the app
    if (currentPage === 'subscription') {
      return <SubscriptionPage onSuccess={(redirectTo, orderDetails) => {
        if (redirectTo === 'thank-you' && orderDetails) {
          reactNavigate('/thank-you', { state: orderDetails });
        } else {
          navigate(redirectTo || 'dashboard');
        }
      }} />;
    }
    
    switch (currentPage) {
      case 'dashboard':
        if (user.role === 'parent') return <ParentDashboard onNavigate={navigate} />;
        if (user.role === 'teacher') return <TeacherDashboard onNavigate={navigate} />;
        return <StudentDashboard onNavigate={navigate} />;
      case 'chapters': return <ChaptersPage params={pageParams} onNavigate={navigate} />;
      case 'video': return <VideoExtractor params={pageParams} onNavigate={navigate} />;
      case 'quiz': return <QuizPage params={pageParams} onNavigate={navigate} />;
      case 'chat': return <ChapterChat params={pageParams} onNavigate={navigate} />;
      case 'mock-exam': return <MockExamPage params={pageParams} onNavigate={navigate} />;
      case 'library': return <VideoLibrary onNavigate={navigate} />;
      case 'audio-video': return <AudioVideoPlayer params={pageParams} onNavigate={navigate} />;
      case 'explainer-video': return <ExplainerVideoPlayer params={pageParams} onNavigate={navigate} />;
      case 'groups': return <GroupStudyPage onNavigate={navigate} />;
      case 'exam-calendar': return <ExamCalendar onNavigate={navigate} />;
      case 'ocr': return <OCRUploadPage onNavigate={navigate} />;
      case 'parent': return <ParentDashboard onNavigate={navigate} />;
      case 'teacher': return <TeacherDashboard onNavigate={navigate} />;
      case 'profile': return <ProfilePage onNavigate={navigate} />;
      // V5 Features
      case 'daily-loop': return <DailyLearningLoop onNavigate={navigate} params={pageParams} />;
      case 'exam-readiness': return <ExamReadiness onNavigate={navigate} />;
      case 'parent-test-generator': return <ParentTestGenerator onNavigate={navigate} />;
      case 'answer-evaluator': return <AnswerSheetEvaluator onNavigate={navigate} />;
      case 'school-scores': return <SchoolScoreTracker onNavigate={navigate} />;
      case 'weekly-report': return <WeeklyReport onNavigate={navigate} />;
      case 'parent-dashboard': return <ParentDashboard onNavigate={navigate} />;
      case 'test-preview': return <TestPreviewPage onNavigate={navigate} params={pageParams} />;
      case 'thank-you': return <ThankYouPage onNavigate={navigate} />;
      default: return <StudentDashboard onNavigate={navigate} />;
    }
  };

  const getNavItems = () => {
    if (user.role === 'student') {
      return [
        { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { id: 'chapters', label: 'Chapters', icon: BookOpen },
        { id: 'exam-calendar', label: 'Exams', icon: Calendar },
        { id: 'groups', label: 'Groups', icon: Users },
        { id: 'profile', label: 'Profile', icon: User },
      ];
    }
    if (user.role === 'parent') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'teacher', label: 'Progress', icon: GraduationCap },
        { id: 'exam-calendar', label: 'Exams', icon: Calendar },
        { id: 'profile', label: 'Profile', icon: User },
      ];
    }
    return [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'profile', label: 'Profile', icon: User },
    ];
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-[#0f172a]">
      {/* Trial Banner */}
      {hasActiveTrial && showTrialBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">
                {trialStatus?.days_remaining} day{trialStatus?.days_remaining !== 1 ? 's' : ''} left in your free trial
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate('subscription')}
                className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full font-medium transition-colors"
                data-testid="trial-upgrade-btn"
              >
                Upgrade Now
              </button>
              <button 
                onClick={() => setShowTrialBanner(false)}
                className="text-white/70 hover:text-white"
                data-testid="trial-dismiss-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={hasActiveTrial && showTrialBanner ? 'pt-10' : ''}>
        {renderPage()}
      </div>
      {!isFullscreen && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-slate-800/50" data-testid="bottom-nav">
          <div className="max-w-lg mx-auto flex items-center justify-around h-16 px-1">
            {navItems.map(item => {
              const isActive = currentPage === item.id ||
                (item.id === 'dashboard' && ['dashboard', 'parent', 'teacher'].includes(currentPage));
              return (
                <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => navigate(item.id)}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all ${isActive ? 'text-violet-400' : 'text-slate-500 hover:text-slate-300'}`}>
                  <item.icon className={`w-5 h-5 ${isActive ? 'text-violet-400' : ''}`} />
                  <span className="text-[9px] font-medium">{item.label}</span>
                  {isActive && <div className="w-1 h-1 rounded-full bg-violet-400 mt-0.5" />}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
